import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { themedColor, Flex } from '@lemonade-hq/cdk';
import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ApiRoute } from '../../../config/api.esm.js';
import { ErrorBoundary } from '../../../providers/ErrorBoundary.esm.js';
import { useCommonI18nContext } from '../../../providers/I18nProvider.esm.js';
import { getAccountDropdownSettings } from '../../../providers/LayoutProvider.esm.js';
import { FloatingAuA } from '../../AuA/FloatingAuA.esm.js';
import { CookieConsent } from '../../CookieConsent/CookieConsent.esm.js';
import { ErrorPage } from '../../ErrorPage/ErrorPage.esm.js';
import { Footer } from '../../Footer/Footer.esm.js';
import { HEADER_HEIGHT, Header } from '../../Header/Header.esm.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _tagged_template_literal(strings, raw) {
    if (!raw) {
        raw = strings.slice(0);
    }
    return Object.freeze(Object.defineProperties(strings, {
        raw: {
            value: Object.freeze(raw)
        }
    }));
}
function _templateObject() {
    var data = _tagged_template_literal([
        "\n      min-height: calc(100vh - ",
        "px);\n      padding-top: ",
        "px;\n    "
    ]);
    _templateObject = function _templateObject() {
        return data;
    };
    return data;
}
function _templateObject1() {
    var data = _tagged_template_literal([
        "\n      border-bottom: 3px solid ",
        ";\n    "
    ]);
    _templateObject1 = function _templateObject1() {
        return data;
    };
    return data;
}
function _templateObject2() {
    var data = _tagged_template_literal([
        "\n  flex-grow: 1;\n  min-height: 100vh;\n  display: flex;\n  flex-direction: column;\n\n  ",
        "\n\n  ",
        "\n"
    ]);
    _templateObject2 = function _templateObject2() {
        return data;
    };
    return data;
}
var Container = styled.div(_templateObject2(), function(param) {
    var showHeader = param.showHeader;
    return showHeader && css(_templateObject(), HEADER_HEIGHT, HEADER_HEIGHT);
}, function(param) {
    var hasBottomBorder = param.hasBottomBorder;
    return hasBottomBorder && css(_templateObject1(), themedColor("primary"));
});
var LemonadeAppUiContainer = /*#__PURE__*/ React.memo(function(param) {
    var error = param.error, layout = param.layout, user = param.user, nav = param.nav, auth = param.auth, children = param.children, customInitialErrorComponent = param.customInitialErrorComponent;
    var _layout_footer, _layout_cookieConsent;
    var s = useCommonI18nContext().s;
    var userFullName = useMemo(function() {
        return user != null ? [
            user.givenName,
            user.familyName
        ].join(" ") : undefined;
    }, [
        user
    ]);
    var showHeader = useMemo(function() {
        var _layout_header;
        return !(layout === null || layout === void 0 ? void 0 : (_layout_header = layout.header) === null || _layout_header === void 0 ? void 0 : _layout_header.hide);
    }, [
        layout
    ]);
    var showFooter = useMemo(function() {
        var _layout_footer;
        return !(layout === null || layout === void 0 ? void 0 : (_layout_footer = layout.footer) === null || _layout_footer === void 0 ? void 0 : _layout_footer.hide);
    }, [
        layout
    ]);
    var showAuA = useMemo(function() {
        var _layout_aua;
        return !(layout === null || layout === void 0 ? void 0 : (_layout_aua = layout.aua) === null || _layout_aua === void 0 ? void 0 : _layout_aua.hide);
    }, [
        layout
    ]);
    var hasError = Boolean(error);
    var RenderedErrorComponent = customInitialErrorComponent !== null && customInitialErrorComponent !== void 0 ? customInitialErrorComponent : ErrorPage;
    var _layout_footer_localeChangeUrl;
    return /*#__PURE__*/ jsx(ErrorBoundary, {
        errorComponent: RenderedErrorComponent,
        report: true,
        children: /*#__PURE__*/ jsxs(Flex, {
            flexDirection: "column",
            minHeight: "100vh",
            children: [
                showHeader && /*#__PURE__*/ jsx(Header, _object_spread_props(_object_spread({}, layout === null || layout === void 0 ? void 0 : layout.header), {
                    accountDropdownOptionsSettings: user != null ? getAccountDropdownSettings({
                        auth: auth,
                        nav: nav,
                        s: s
                    }) : undefined,
                    showUserAccount: true
                })),
                /*#__PURE__*/ jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ jsx(Container, {
                            hasBottomBorder: hasError,
                            showHeader: showHeader,
                            children: hasError ? /*#__PURE__*/ jsx(RenderedErrorComponent, {}) : children
                        }),
                        showFooter && /*#__PURE__*/ jsx(Footer, {
                            localeChangeLanguageOnly: user != null,
                            localeChangeRedirectsWithSuffixes: false,
                            localeChangeUrl: (_layout_footer_localeChangeUrl = layout === null || layout === void 0 ? void 0 : (_layout_footer = layout.footer) === null || _layout_footer === void 0 ? void 0 : _layout_footer.localeChangeUrl) !== null && _layout_footer_localeChangeUrl !== void 0 ? _layout_footer_localeChangeUrl : ApiRoute.UpdateLocale
                        }),
                        showAuA && /*#__PURE__*/ jsx(FloatingAuA, _object_spread_props(_object_spread({}, layout === null || layout === void 0 ? void 0 : layout.aua), {
                            prefilledEmail: user === null || user === void 0 ? void 0 : user.email,
                            prefilledName: userFullName
                        })),
                        !(layout === null || layout === void 0 ? void 0 : (_layout_cookieConsent = layout.cookieConsent) === null || _layout_cookieConsent === void 0 ? void 0 : _layout_cookieConsent.hide) && /*#__PURE__*/ jsx(CookieConsent, {
                            baseUrl: nav.monolithUrl
                        })
                    ]
                })
            ]
        })
    });
});

export { LemonadeAppUiContainer };
